import { hasWindow, sleep } from "@litres/utils";
import { type RetryOptions } from "@reduxjs/toolkit/dist/query/retry";
import type { RetryingOptions, RetryingRequestOptions } from "./types";
import type { AxiosBaseQueryError } from "../baseQuery";
import {
  getUniversalError,
  OFFLINE_STATUS_ERROR,
  CLIENT_TIMEOUT_STATUS_ERROR,
} from "./error";

const statusCodesForRepeatingRequest = [500, 501, 502, 503, 504];

const defaultClientRetryOptions: RetryingOptions = {
  maxRetry: 3,
  statusCodes: statusCodesForRepeatingRequest,
  delay: 3000,
  shouldRetryOnNetworkError: true,
  shouldRetryOnTimeoutError: true,
};

const defaultServerRetryOptions: RetryingOptions = {
  maxRetry: 2,
  statusCodes: statusCodesForRepeatingRequest,
  delay: 2000,
  shouldRetryOnNetworkError: false,
  shouldRetryOnTimeoutError: true,
};

function getRetryOptionsBasedOnEnvironment(
  options: Partial<RetryingRequestOptions>
) {
  if (hasWindow) {
    const clientOptions = options.client ?? {};
    return Object.assign({}, defaultClientRetryOptions, clientOptions);
  }

  const serverOptions = options.server ?? {};
  return Object.assign({}, defaultServerRetryOptions, serverOptions);
}

/**
  Функция обновляет поле statusCodes (мутация) в зависимости от параметров:
  - shouldRetryOnNetworkError (code 53)
  - shouldRetryOnTimeoutError (code 54)
*/
function updateStatusCodesByOptions(options: RetryingOptions) {
  const statuses = [];

  if (options.shouldRetryOnNetworkError) {
    statuses.push(OFFLINE_STATUS_ERROR);
  }

  if (options.shouldRetryOnTimeoutError) {
    statuses.push(CLIENT_TIMEOUT_STATUS_ERROR);
  }

  if (statuses.length === 0) {
    return;
  }

  options.statusCodes = options.statusCodes.concat(statuses);
}

function getRetryOptions(options: Partial<RetryingRequestOptions> = {}) {
  const currentOpts = getRetryOptionsBasedOnEnvironment(options);
  updateStatusCodesByOptions(currentOpts);

  return currentOpts;
}

/**
 * Создает retry конфигурацию. Вызов без аргументов приведет с созданию базовой конфигурации.
 * Вызов с аргументами переопределит базовые настройки.
 * 
 * @example
 * endpoints: build => ({
     request: build.query({
       query: ...,
       extraOptions: {
         ...createRetryingRequestOptions({
          server: {
            maxRetry: 10
          }
         }),
       },
     }),
   }),
 * 
 */
export const createRetryingRequestOptions = (
  options: Partial<RetryingRequestOptions> = {}
): RetryOptions => {
  const currentOptions = getRetryOptions(options);

  return {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    retryCondition(err, _args, extraArgs) {
      const { attempt } = extraArgs;
      const error = getUniversalError(err as AxiosBaseQueryError);

      const isStatusInCodes =
        typeof error.status === "number" &&
        currentOptions.statusCodes.includes(error.status);

      const isWithinRetryLimit = attempt <= currentOptions.maxRetry;

      return isStatusInCodes && isWithinRetryLimit;
    },
    backoff: async () => {
      await sleep(currentOptions.delay);
    },
  };
};

/**
 * Конфигурация, которая использует значения по умолчанию.
 *
 * _server options_:
 * - _Максимальное кол-во попыток_: `2`;
 * - _Задержка между запросами_: `2_000 ms`;
 * - _Проверяемые статусы ошибок_: `[500, 501, 502, 503, 504]`;
 * - _Проверка на timeout_: `включено`;
 * - _Проверка на состояние сети `(online / offline)`_: `выключено`;
 *
 * _client options_:
 * - _Максимальное кол-во попыток_: `3`;
 * - _Задержка между запросами_: `3_000 ms`;
 * - _Проверяемые статусы ошибок_: `[500, 501, 502, 503, 504]`;
 * - _Проверка на timeout_: `включено`;
 * - _Проверка на состояние сети `(online / offline)`_: `включено`;
 *
 * @example
 * endpoints: build => ({
     request: build.query({
       query: ...,
       extraOptions: {
         ...retryingRequestOptions,
       },
     }),
   }),
 * 
 */
export const standardRetryingRequestOptions = createRetryingRequestOptions();
